import React, { useEffect, useState } from "react";
import HeaderBar from "components/ui/HeaderBar";
import RoleBasedSidebar from "components/ui/RoleBasedSidebar";
import BreadcrumbNavigation from "components/ui/BreadcrumbNavigation";
import Icon from "components/AppIcon";
import StatusIndicatorBadge from "components/ui/StatusIndicatorBadge";
import { specialQuotaAdminAPI } from "utils/api";
import { useAuth } from "context/AuthContext";
import Notifier from "components/ui/Notifier";
import { formatDate } from "utils/dateFormatter";
import PaginationComponent from "components/PaginationComponent";

const TAB_STATUS = {
  pending: ["pending_quota_review"],
  approved: ["ready_for_selection", "selection_approved"],
};

const PAGE_SIZE = 10;

const AdminSpecialQuotas = () => {
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [quotas, setQuotas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedQuota, setSelectedQuota] = useState(null);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptNo, setReceiptNo] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [notify, setNotify] = useState({
    show: false,
    type: "info",
    message: "",
  });
  const [activeTab, setActiveTab] = useState("pending");
  const [confirmationModal, setConfirmationModal] = useState({
    type: null,
    quota: null,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: PAGE_SIZE,
    total: 0,
    lastPage: 1,
    from: 0,
    to: 0,
  });
  const [tabPages, setTabPages] = useState({
    pending: 1,
    approved: 1,
  });
  const [search, setSearch] = useState("");
  const [editQuota, setEditQuota] = useState(null);
  const [editForm, setEditForm] = useState({
    nationality: "",
    quotaReferenceNo: "",
    quotaApproved: "",
    approvedDate: "",
  });
  const [editFile, setEditFile] = useState(null);
  const [editError, setEditError] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const tabOptions = [
    { key: "pending", label: "Pending Review" },
    { key: "approved", label: "Approved Quotas" },
  ];

  // Normalise a stored date (ISO / Date) to the YYYY-MM-DD an <input type="date"> expects.
  const toDateInputValue = (value) => {
    if (!value) return "";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toISOString().slice(0, 10);
  };

  // Mirror of the employer form rule: start with alphanumeric; allow letters,
  // numbers, spaces, ".", "/", "-", "(", ")"; end with alphanumeric or ")".
  const quotaRefIsValid = (value) => {
    const trimmed = (value || "").trim();
    if (!trimmed) return false;
    return /^[A-Za-z0-9](?:[A-Za-z0-9/\-.()\s]*[A-Za-z0-9)])?$/.test(trimmed);
  };

  const openEdit = (quota) => {
    setEditError(null);
    setEditFile(null);
    setEditForm({
      nationality: quota.nationality || "",
      quotaReferenceNo:
        quota.quotaReferenceNo || quota.quota_reference_no || "",
      quotaApproved: String(quota.quotaApproved ?? quota.quota_approved ?? ""),
      approvedDate: toDateInputValue(quota.approvedDate || quota.approved_date),
    });
    setEditQuota(quota);
  };

  const handleEditSave = async () => {
    if (!editQuota) return;
    const nationality = editForm.nationality.trim();
    const quotaReferenceNo = editForm.quotaReferenceNo.trim();
    const quotaApproved = parseInt(editForm.quotaApproved, 10);

    if (
      !nationality ||
      !quotaReferenceNo ||
      !editForm.quotaApproved ||
      !editForm.approvedDate
    ) {
      setEditError("Please fill in all required fields.");
      return;
    }
    if (!quotaRefIsValid(quotaReferenceNo)) {
      setEditError(
        'Quota Reference No. must start with a letter or number, may include letters, numbers, spaces, ".", "/", "-", "(" and ")", and must end with a letter, number or ")".'
      );
      return;
    }
    if (!Number.isInteger(quotaApproved) || quotaApproved <= 0) {
      setEditError("No. of Quota Approved must be a positive whole number.");
      return;
    }

    try {
      setSavingEdit(true);
      setEditError(null);
      let slipPayload = {};
      if (editFile) {
        const upload = await specialQuotaAdminAPI.uploadSlip(editFile);
        slipPayload = {
          quota_slip_key: upload?.key || upload?.fileKey,
          quota_slip_url: upload?.url || upload?.signedUrl,
        };
      }
      await specialQuotaAdminAPI.updateQuota(editQuota.id || editQuota.vdrId, {
        nationality,
        quota_reference_no: quotaReferenceNo,
        quota_approved: quotaApproved,
        approved_date: editForm.approvedDate,
        ...slipPayload,
      });
      setNotify({
        show: true,
        type: "success",
        message: "Special quota updated.",
      });
      setEditQuota(null);
      setEditFile(null);
      loadQuotas(activeTab);
    } catch (err) {
      console.error("Update quota failed", err);
      setEditError(
        err.response?.data?.error || err.message || "Failed to update quota."
      );
    } finally {
      setSavingEdit(false);
    }
  };

  useEffect(() => {
    if (
      user &&
      user.role &&
      !["admin", "master_admin", "clab", "clab_user", 1, "1"].includes(
        user.role
      )
    ) {
      setError("You do not have access to this page.");
    }
  }, [user]);

  useEffect(() => {
    if (selectedQuota) {
      setReceiptNo(selectedQuota.receiptNo || selectedQuota.receipt_no || "");
    } else {
      setReceiptNo("");
      setRejectReason("");
      setReceiptFile(null);
    }
  }, [selectedQuota]);

  const loadQuotas = async (tabKey = activeTab, pageOverride) => {
    try {
      setLoading(true);
      setError(null);
      const statuses = TAB_STATUS[tabKey] || TAB_STATUS.pending;
      const targetPage = pageOverride || tabPages[tabKey] || 1;
      const response = await specialQuotaAdminAPI.getPendingQuotas({
        status: statuses,
        page: targetPage,
        per_page: PAGE_SIZE,
        ...(search ? { search } : {}),
      });
      const payload = response || {};
      const records = payload.data?.data || payload.data || [];
      const meta = payload.data?.pagination || payload.pagination || {};
      const resolvedPage = meta.page || targetPage;

      setQuotas(Array.isArray(records) ? records : []);
      setPagination({
        page: resolvedPage,
        perPage: meta.per_page || PAGE_SIZE,
        total: meta.total ?? records.length,
        lastPage: meta.last_page || 1,
        from:
          meta.from ??
          (records.length > 0
            ? (resolvedPage - 1) * (meta.per_page || PAGE_SIZE) + 1
            : 0),
        to:
          meta.to ??
          (records.length > 0
            ? Math.min(
                resolvedPage * (meta.per_page || PAGE_SIZE),
                meta.total ?? records.length
              )
            : 0),
      });
      setTabPages((prev) => ({
        ...prev,
        [tabKey]: resolvedPage,
      }));
    } catch (err) {
      console.error("Failed to load special quotas", err);
      setError(
        err.response?.data?.error || err.message || "Failed to load quotas."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQuotas(activeTab, 1);
  }, [activeTab]);

  useEffect(() => {
    // Reset page on search change
    setTabPages((prev) => ({ ...prev, [activeTab]: 1 }));
    loadQuotas(activeTab, 1);
  }, [search]);

  const handlePageChange = (newPage) => {
    if (loading) return;
    const totalPages = pagination.lastPage || 1;
    if (newPage < 1 || newPage > totalPages) return;
    loadQuotas(activeTab, newPage);
  };

  const handleSidebarToggle = () => setSidebarCollapsed(!sidebarCollapsed);
  const requestApprove = (quota) => {
    if (!receiptNo || !receiptNo.trim()) {
      setNotify({
        show: true,
        type: "error",
        message: "Please provide the receipt number before approving.",
      });
      return;
    }
    setConfirmationModal({ type: "approve", quota });
  };

  const requestReject = (quota) => {
    if (!rejectReason || !rejectReason.trim()) {
      setNotify({
        show: true,
        type: "error",
        message: "Please provide a rejection reason.",
      });
      return;
    }
    setConfirmationModal({ type: "reject", quota });
  };

  const closeConfirmationModal = () => {
    setConfirmationModal({ type: null, quota: null });
  };

  const handleApprove = async (quota) => {
    if (!quota?.id && !quota?.vdrId) return;
    try {
      setSubmitting(true);
      let receiptPayload = {};
      if (receiptFile) {
        const upload = await specialQuotaAdminAPI.uploadReceipt(receiptFile);
        receiptPayload = {
          receipt_key: upload?.key || upload?.fileKey,
          receipt_url: upload?.url || upload?.signedUrl,
        };
      }
      await specialQuotaAdminAPI.approveQuota(quota.id || quota.vdrId, {
        receipt_no: receiptNo,
        ...receiptPayload,
      });
      setNotify({
        show: true,
        type: "success",
        message: "Quota approved and ready for selection.",
      });
      setReceiptFile(null);
      setReceiptNo("");
      setSelectedQuota(null);
      loadQuotas(activeTab);
    } catch (err) {
      console.error("Approve quota failed", err);
      setNotify({
        show: true,
        type: "error",
        message:
          err.response?.data?.error ||
          err.message ||
          "Failed to approve quota.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async (quota) => {
    if (!quota?.id && !quota?.vdrId) return;
    try {
      setSubmitting(true);
      await specialQuotaAdminAPI.rejectQuota(quota.id || quota.vdrId, {
        reason: rejectReason,
      });
      setNotify({ show: true, type: "success", message: "Quota rejected." });
      setRejectReason("");
      setSelectedQuota(null);
      loadQuotas(activeTab);
    } catch (err) {
      console.error("Reject quota failed", err);
      setNotify({
        show: true,
        type: "error",
        message:
          err.response?.data?.error || err.message || "Failed to reject quota.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmAction = async () => {
    const { type, quota } = confirmationModal;
    if (!type || !quota) return;
    try {
      if (type === "approve") {
        await handleApprove(quota);
      } else if (type === "reject") {
        await handleReject(quota);
      }
    } finally {
      closeConfirmationModal();
    }
  };

  const isPendingTab = activeTab === "pending";
  const noDataMessage = isPendingTab
    ? "No pending special quotas."
    : "No approved special quotas yet.";
  const normalizedSelectedStatus = selectedQuota
    ? String(selectedQuota.status || "").toLowerCase()
    : "";
  const isPendingQuota = selectedQuota
    ? TAB_STATUS.pending.includes(normalizedSelectedStatus)
    : false;

  return (
    <div className="min-h-screen bg-background">
      <HeaderBar
        userRole="admin"
        onSidebarToggle={handleSidebarToggle}
        userName={user?.fullName || "Admin"}
      />
      <Notifier
        type={notify.type}
        message={notify.message}
        show={notify.show}
        onClose={() => setNotify((prev) => ({ ...prev, show: false }))}
        duration={3000}
      />
      <RoleBasedSidebar
        userRole="admin"
        isCollapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
      />

      <main
        className={`transition-all duration-300 ease-smooth pt-16 ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
        }`}
      >
        <div className="p-6 max-w-7xl mx-auto space-y-6">
          <BreadcrumbNavigation userRole="admin" />
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-text-primary">
                Special Quota Review
              </h1>
              <p className="text-text-secondary">
                Review and approve eQuotaSpecial submissions from employers.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="text"
                placeholder="Search company or quota ref..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="border border-secondary-300 rounded px-3 py-2 text-sm w-64"
              />
              <button
                onClick={() => loadQuotas(activeTab, 1)}
                className="flex items-center space-x-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Icon name="RefreshCw" size={18} />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          <div className="border-b border-secondary-200 flex space-x-6">
            {tabOptions.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`py-3 px-2 text-sm font-medium border-b-2 transition-colors ${
                    isActive
                      ? "text-primary border-primary"
                      : "text-text-secondary border-transparent hover:text-text-primary"
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          {error && (
            <div className="bg-error-50 border border-error-200 text-error p-4 rounded-lg">
              {error}
            </div>
          )}

          {loading ? (
            <div className="bg-white border border-secondary-200 rounded-lg p-8 text-center">
              <Icon
                name="Loader"
                size={48}
                className="animate-spin text-primary mx-auto mb-4"
              />
              <p className="text-text-secondary">Loading special quotas...</p>
            </div>
          ) : quotas.length === 0 ? (
            <div className="bg-white border border-secondary-200 rounded-lg p-8 text-center text-text-secondary">
              {noDataMessage}
            </div>
          ) : (
            <div className="card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-secondary-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">
                        VDR
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">
                        Employer
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">
                        Nationality
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">
                        Quota
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">
                        Slip
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">
                        Receipt
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-text-primary">
                        Status
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-text-primary">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotas.map((quota) => (
                      <tr
                        key={quota.id || quota.vdrId}
                        className="border-b border-secondary-100 hover:bg-secondary-25 transition-colors"
                      >
                        <td className="px-6 py-4 align-top">
                          <div className="space-y-1">
                            <div className="font-semibold text-text-primary text-base">
                              {quota.vdrId || "Special Quota"}
                            </div>
                            <div className="text-xs text-text-secondary">
                              Quota Ref:{" "}
                              {quota.quotaReferenceNo ||
                                quota.quota_reference_no}
                            </div>
                            <div className="text-xs text-text-secondary">
                              Approved Date:{" "}
                              {formatDate(
                                quota.approvedDate || quota.approved_date
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 align-top text-text-secondary">
                          <div className="text-text-primary font-medium">
                            {quota.companyName || quota.employerName || "—"}
                          </div>
                          <div className="text-xs text-text-secondary">
                            Employer ID:{" "}
                            {quota.employerId || quota.employer_id || "—"}
                          </div>
                        </td>
                        <td className="px-6 py-4 align-top text-text-primary">
                          {quota.nationality || "—"}
                        </td>
                        <td className="px-6 py-4 align-top">
                          <div className="text-text-primary font-semibold">
                            {quota.quotaApproved || quota.quota_approved}
                          </div>
                          <div className="text-xs text-text-secondary">
                            Reserved cap:{" "}
                            {(quota.quotaApproved ||
                              quota.quota_approved ||
                              0) * 2}
                          </div>
                        </td>
                        <td className="px-6 py-4 align-top">
                          {quota.quotaSlipUrl || quota.quota_slip_url ? (
                            <a
                              href={quota.quotaSlipUrl || quota.quota_slip_url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center px-3 py-1.5 bg-secondary-100 text-text-primary rounded-lg hover:bg-secondary-200"
                            >
                              <Icon
                                name="ExternalLink"
                                size={14}
                                className="mr-1"
                              />
                              View Slip
                            </a>
                          ) : (
                            <span className="text-xs text-text-tertiary">
                              No slip
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 align-top">
                          {quota.receiptUrl || quota.receipt_url ? (
                            <a
                              href={quota.receiptUrl || quota.receipt_url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center px-3 py-1.5 bg-success-50 text-success-700 rounded-lg hover:bg-success-100"
                            >
                              <Icon
                                name="FileText"
                                size={14}
                                className="mr-1"
                              />
                              View Receipt
                            </a>
                          ) : (
                            <span className="text-xs text-text-tertiary">
                              No receipt
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 align-top text-center">
                          <StatusIndicatorBadge
                            status={quota.status || "pending"}
                            size="sm"
                          />
                        </td>
                        <td className="px-6 py-4 align-top text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => setSelectedQuota(quota)}
                              className="inline-flex items-center px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary-700 text-sm"
                            >
                              <Icon
                                name={isPendingTab ? "CheckSquare" : "Eye"}
                                size={14}
                                className="mr-1"
                              />
                              {isPendingTab ? "Review" : "View"}
                            </button>
                            {!isPendingTab && (
                              <button
                                onClick={() => openEdit(quota)}
                                className="inline-flex items-center px-3 py-1.5 bg-secondary-100 text-text-primary rounded-lg hover:bg-secondary-200 text-sm"
                                title="Correct quota reference, count, date or slip"
                              >
                                <Icon name="Pencil" size={14} className="mr-1" />
                                Edit
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {pagination.total > 0 && (
                <div className="px-6 py-4 border-t border-secondary-100 bg-secondary-25">
                  <PaginationComponent
                    pagination={{
                      currentPage: pagination.page,
                      perPage: pagination.perPage,
                      total: pagination.total,
                      totalPages: pagination.lastPage,
                      from: pagination.from,
                      to: pagination.to,
                    }}
                    onPageChange={handlePageChange}
                    showPerPageSelector={false}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {selectedQuota && (
        <div className="fixed inset-0 bg-secondary-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-xl font-semibold text-text-primary">
                  Review Special Quota
                </h3>
                <p className="text-sm text-text-secondary">
                  {selectedQuota.vdrId} • {selectedQuota.companyName || "—"}
                </p>
              </div>
              <button
                onClick={() => setSelectedQuota(null)}
                className="text-text-tertiary hover:text-text-primary"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm text-text-secondary">
              <div>
                <div className="text-text-primary font-medium">Quota Ref</div>
                <div>
                  {selectedQuota.quotaReferenceNo ||
                    selectedQuota.quota_reference_no ||
                    "—"}
                </div>
              </div>
              <div>
                <div className="text-text-primary font-medium">
                  Quota Approved
                </div>
                <div>
                  {selectedQuota.quotaApproved ||
                    selectedQuota.quota_approved ||
                    "—"}
                </div>
              </div>
              <div>
                <div className="text-text-primary font-medium">
                  Approved Date
                </div>
                <div>
                  {formatDate(
                    selectedQuota.approvedDate || selectedQuota.approved_date
                  )}
                </div>
              </div>
              <div>
                <div className="text-text-primary font-medium">Nationality</div>
                <div>{selectedQuota.nationality || "—"}</div>
              </div>
              <div>
                <div className="text-text-primary font-medium">Status</div>
                <div className="mt-1">
                  <StatusIndicatorBadge
                    status={selectedQuota.status || "pending"}
                    size="sm"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-text-primary font-medium">Quota Slip</div>
                {selectedQuota.quotaSlipUrl || selectedQuota.quota_slip_url ? (
                  <a
                    href={
                      selectedQuota.quotaSlipUrl || selectedQuota.quota_slip_url
                    }
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center px-3 py-1 bg-secondary-100 text-text-primary rounded-lg hover:bg-secondary-200 text-sm"
                  >
                    <Icon name="ExternalLink" size={14} className="mr-1" />
                    View
                  </a>
                ) : (
                  <span className="text-xs text-text-tertiary">
                    Not provided
                  </span>
                )}
              </div>
            </div>

            {isPendingQuota ? (
              <>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                      Receipt No.
                    </label>
                    <input
                      type="text"
                      value={receiptNo}
                      onChange={(e) => setReceiptNo(e.target.value)}
                      className="w-full px-3 py-2 border border-secondary-200 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary"
                      placeholder="Enter receipt number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                      Upload Receipt
                    </label>
                    <input
                      type="file"
                      onChange={(e) =>
                        setReceiptFile(e.target.files?.[0] || null)
                      }
                      className="w-full text-sm"
                    />
                    <p className="text-xs text-text-tertiary mt-1">
                      Optional but recommended. Stored privately.
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    Reject Reason
                  </label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-secondary-200 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary resize-none"
                    placeholder="If rejecting, provide reason"
                  />
                </div>
              </>
            ) : (
              <div className="rounded-lg bg-secondary-50 border border-secondary-100 p-4 text-sm text-text-secondary space-y-1">
                <div className="text-text-primary font-medium mb-1">
                  Approval Details
                </div>
                <div>
                  Receipt No.:{" "}
                  {selectedQuota.receiptNo || selectedQuota.receipt_no || "—"}
                </div>
                {selectedQuota.receiptUrl || selectedQuota.receipt_url ? (
                  <a
                    href={selectedQuota.receiptUrl || selectedQuota.receipt_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center text-primary text-sm mt-2"
                  >
                    <Icon name="ExternalLink" size={14} className="mr-1" />
                    View Receipt
                  </a>
                ) : null}
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={() => setSelectedQuota(null)}
                className="px-4 py-2 bg-secondary-100 text-text-primary rounded-lg hover:bg-secondary-200"
              >
                Close
              </button>
              {isPendingQuota ? (
                <>
                  <button
                    onClick={() => requestReject(selectedQuota)}
                    disabled={submitting}
                    className="px-4 py-2 bg-error-50 text-error rounded-lg hover:bg-error-100 disabled:opacity-60"
                  >
                    {submitting ? "Processing..." : "Reject"}
                  </button>
                  <button
                    onClick={() => requestApprove(selectedQuota)}
                    disabled={submitting}
                    className="px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 disabled:opacity-60 flex items-center"
                  >
                    {submitting && (
                      <Icon
                        name="Loader"
                        size={16}
                        className="animate-spin mr-2"
                      />
                    )}
                    Approve
                  </button>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {confirmationModal.type && confirmationModal.quota && (
        <div className="fixed inset-0 bg-secondary-900 bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-xl font-semibold text-text-primary">
                  {confirmationModal.type === "approve"
                    ? "Confirm Approval"
                    : "Confirm Rejection"}
                </h3>
                <p className="text-sm text-text-secondary mt-1">
                  {confirmationModal.quota.vdrId ||
                    confirmationModal.quota.quotaReferenceNo ||
                    "Special Quota"}
                </p>
              </div>
              <button
                onClick={closeConfirmationModal}
                className="text-text-tertiary hover:text-text-primary"
              >
                <Icon name="X" size={18} />
              </button>
            </div>
            <div className="text-sm text-text-secondary space-y-2">
              {confirmationModal.type === "approve" ? (
                <>
                  <p>
                    This action will approve the quota and move it to the
                    ready-for-selection list.
                  </p>
                  <p className="font-medium text-text-primary">
                    Receipt No.: {receiptNo}
                  </p>
                  {receiptFile && (
                    <p className="text-xs text-text-tertiary">
                      Receipt file attached: {receiptFile.name}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p>
                    This action will reject the quota request and notify the
                    employer.
                  </p>
                  <p className="font-medium text-text-primary">
                    Reason: {rejectReason}
                  </p>
                </>
              )}
            </div>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={closeConfirmationModal}
                className="px-4 py-2 bg-secondary-100 text-text-primary rounded-lg hover:bg-secondary-200"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAction}
                disabled={submitting}
                className={`px-4 py-2 rounded-lg text-white ${
                  confirmationModal.type === "approve"
                    ? "bg-primary hover:bg-primary-700"
                    : "bg-error hover:bg-error-600"
                } disabled:opacity-60`}
              >
                {submitting ? "Processing..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {editQuota && (
        <div className="fixed inset-0 bg-secondary-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-xl font-semibold text-text-primary">
                  Edit Special Quota
                </h3>
                <p className="text-sm text-text-secondary">
                  {editQuota.vdrId} • {editQuota.companyName || "—"}
                </p>
              </div>
              <button
                onClick={() => setEditQuota(null)}
                className="text-text-tertiary hover:text-text-primary"
                disabled={savingEdit}
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            <div className="rounded-lg bg-warning-50 border border-warning-200 text-warning-800 text-xs px-3 py-2">
              Editing an approved quota. Changes take effect immediately for the
              employer's selection screen.
            </div>

            {editError && (
              <div className="bg-error-50 border border-error-200 text-error p-3 rounded-lg text-sm">
                {editError}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  Nationality <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  value={editForm.nationality}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      nationality: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary"
                  placeholder="e.g. Bangladesh"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  Quota Reference No. <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  value={editForm.quotaReferenceNo}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      quotaReferenceNo: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary"
                  placeholder="e.g. QA/SQ/BGD/2026/100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  No. of Quota Approved <span className="text-error">*</span>
                </label>
                <input
                  type="number"
                  min="1"
                  value={editForm.quotaApproved}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      quotaApproved: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary"
                  placeholder="e.g. 70"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  Approved Date <span className="text-error">*</span>
                </label>
                <input
                  type="date"
                  value={editForm.approvedDate}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      approvedDate: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-text-primary">
                  Quota Slip
                </label>
                {editQuota.quotaSlipUrl || editQuota.quota_slip_url ? (
                  <a
                    href={editQuota.quotaSlipUrl || editQuota.quota_slip_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center text-primary text-sm"
                  >
                    <Icon name="ExternalLink" size={14} className="mr-1" />
                    View current
                  </a>
                ) : (
                  <span className="text-xs text-text-tertiary">
                    No slip attached
                  </span>
                )}
              </div>
              <input
                type="file"
                onChange={(e) => setEditFile(e.target.files?.[0] || null)}
                className="w-full text-sm"
              />
              <p className="text-xs text-text-tertiary mt-1">
                Leave empty to keep the current slip. Upload a file to replace
                it (max 5MB).
              </p>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={() => setEditQuota(null)}
                className="px-4 py-2 bg-secondary-100 text-text-primary rounded-lg hover:bg-secondary-200"
                disabled={savingEdit}
              >
                Cancel
              </button>
              <button
                onClick={handleEditSave}
                disabled={savingEdit}
                className="px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 disabled:opacity-60 flex items-center"
              >
                {savingEdit && (
                  <Icon
                    name="Loader"
                    size={16}
                    className="animate-spin mr-2"
                  />
                )}
                {savingEdit ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSpecialQuotas;
